import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { error as logError } from '../../../utils/logger';
import { genId } from '../../../utils/genId';
import { dataLayer } from '../../../data/dataLayer';
import { Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers, Target, MessageSquare, X } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition, MODULES, type ModuleId, getModuleIcon } from '../../../content';
import { DjayNarrator } from '../../../mascot';
interface CreatePracticePanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebieden'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreatePracticePanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreatePracticePanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedModuleIds, setSelectedModuleIds] = useState<ModuleId[]>([]);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hidePracticeTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hidePracticeTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedModuleIds([]);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setNote('');
      setTitle('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
    }
  }, [open]);
  useEffect(() => {
    // When modules change, clear families that are no longer in selected modules
    setSelectedFamilyIds(prev => {
      const validFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []).map(f => f.familyId);
      return prev.filter(id => validFamilies.includes(id));
    });
  }, [selectedModuleIds]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (selectedModuleIds.length === 0) return;
    setIsSubmitting(true);
    const assignmentId = genId('practice');
    const wholeClass = selectionMode === 'whole';
    const finalTitle = title.trim() || 'Huiswerk Oefenen';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      title: finalTitle,
      moduleIds: selectedModuleIds,
      targetFamilyIds: selectedFamilyIds,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      questionCount: 20,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const,
      completedByStudentIds: [],
      note: note.trim() || undefined
    };
    try {
      await dataLayer.savePracticeAssignment(assignmentId, assignment);
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save practice assignment', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleModule = (id: ModuleId) => {
    setSelectedModuleIds(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && selectedModuleIds.length === 0) return true;
    // Step 2 is optional (can just assign whole modules)
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-9220" data-mp="1w8ai9t6rgypi">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-9221" data-mp="kslwv695o7qt" data-mpi={i}>
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-indigo-500' : 'bg-slate-200'}`} data-id="element-9222" data-mp="kr133iuo0su0" />}
            <div className="flex flex-col items-center gap-1" data-id="element-9223" data-mp="2rfjhmk4r8js3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-indigo-500 text-white' : isActive ? 'bg-indigo-500 text-white ring-4 ring-indigo-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-9224" data-mp="2mi1kdr03nszx">
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-9225" data-mp="28qwfoahvkaiq" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-indigo-600' : isDone ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9226" data-mp="23ra2g121qu9x">
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;
  const renderSummaryStrip = () => {
    const hasModules = selectedModuleIds.length > 0;
    const hasFamilies = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasModules && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-9227" data-mp="gnihq5jqb07">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-9228" data-mp="e9v9bflsz2gu">
          {hasModules && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9229" data-mp="3dp9u015jngg5">
              <Target className="w-3 h-3 text-slate-500" data-id="element-9230" data-mp="3hqbtmj3dhuin" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9231" data-mp="3hsy1yky38e1x">
                {selectedModuleIds.length} vakken
              </span>
            </div>}
          {hasFamilies ? <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9232" data-mp="3rmydp5nvkhjm">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-9233" data-mp="16yts8exr28n7" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9234" data-mp="16x90ex2tl3t1">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div> : hasModules && <div className="flex items-center gap-1.5 bg-amber-50 rounded-full px-2.5 py-1 border border-amber-200" title="Geen specifieke onderdelen gekozen — leerlingen krijgen vragen uit alle onderdelen van de gekozen vakken" data-id="element-9235" data-mp="1o33zlblh4s62">
                <Layers className="w-3 h-3 text-amber-600" data-id="element-9236" data-mp="2bpirwatcofhi" />
                <span className="text-xs font-semibold text-amber-800" data-id="element-9237" data-mp="26s0rx9xb9aln">
                  Alle onderdelen
                </span>
              </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9238" data-mp="2fsozpmw42eq3">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-9239" data-mp="qcpjxmxzlr1h" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9240" data-mp="lefl92d208b4">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9241" data-mp="2o9frwom6at09">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-9242" data-mp="2saj5f4gr0cyb" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9243" data-mp="2s8yfxt9ajk7b">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-9244" data-mp="29bf12905j7cz">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-9245" data-mp="29cgo7bufzkb2">
        <p className="text-sm font-medium text-slate-600" data-id="element-9246" data-mp="3iyt5y18ixx4">
          Kies één of meerdere vakgebieden om te oefenen voor{' '}
          <span className="font-bold text-slate-900" data-id="element-9247" data-mp="7kaalg9ygcu8">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex gap-3 relative" data-id="element-9248" data-mp="203lqctjgputl">
          <button onClick={dismissTip} className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-600" data-id="element-9249" data-mp="1lf8zgaz8f25y">
            <X className="w-4 h-4" data-id="element-9250" data-mp="1qelrda4ymzfe" />
          </button>
          <div className="mt-1" data-id="element-9251" data-mp="2e34yvng6r86f">
            <DjayNarrator size="xs" emotion="happy" data-id="element-9252" data-mp="2e468rolgtomk" />
          </div>
          <div className="text-sm text-indigo-800 pr-6" data-id="element-9253" data-mp="2j2zntlgehnou">
            <span className="font-bold" data-id="element-9254" data-mp="2j0n5362tj9aq">Tip van Djay:</span> Modules zijn de
            onderwerpen binnen een vak — bijvoorbeeld 'Breuken' of
            'Werkwoordspelling'.
          </div>
        </div>}

      <div className="grid grid-cols-2 gap-2.5 flex-1 content-start" data-id="element-9255" data-mp="tnt5twz9cf68">
        {MODULES.map(module => {
        const isSelected = selectedModuleIds.includes(module.id);
        const IconComp = getModuleIcon(module.icon);
        return <div key={module.id} onClick={() => toggleModule(module.id)} className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex flex-col items-center text-center ${isSelected ? 'border-indigo-500 bg-indigo-50/60 shadow-sm' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9256" data-mp="q4km6kjawe3j">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-9257" data-mp="7fe1wt2uzefm">
                <IconComp className="w-5 h-5" data-id="element-9258" data-mp="h4xkryqkn8ah" />
              </div>
              <h3 className={`font-bold text-sm ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-9259" data-mp="h7aawz6hgoym">
                {module.title}
              </h3>
              {isSelected && <div className="absolute top-2 right-2" data-id="element-9260" data-mp="3fv7gv3hjkhuk">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500" data-id="element-9261" data-mp="3fuef6gxfwija" />
                </div>}
            </div>;
      })}
      </div>
    </div>;
  const renderStep2 = () => {
    const allFamilies = selectedModuleIds.flatMap(modId => FAMILIES_BY_MODULE.get(modId) || []);
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-9262" data-mp="22qedjy4bxp87">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-9263" data-mp="5w9rlbvc3peg">
          <p className="text-sm font-medium text-slate-600" data-id="element-9264" data-mp="5tnlzhs07jyv">
            Kies welke onderdelen je leerlingen oefenen.
          </p>
        </div>

        {selectedCount === 0 && <div className="mb-3 flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200" data-id="element-9265" data-mp="8ye81c16zfh5">
            <div className="mt-0.5 text-amber-600" data-id="element-9266" data-mp="dumtt89wa6xa">
              <Target className="w-4 h-4" data-id="element-9267" data-mp="3v5mcmc4afic4" />
            </div>
            <div className="text-xs text-amber-900 leading-relaxed" data-id="element-9268" data-mp="3yjibbt5kvxo">
              <span className="font-bold" data-id="element-9269" data-mp="3wowzwdyz1t2">Nog niets gekozen.</span> Zonder
              selectie krijgen de leerlingen vragen uit{' '}
              <span className="font-bold" data-id="element-9270" data-mp="3r3981r1a1qgx">
                alle {allFamilies.length} onderdelen
              </span>{' '}
              van de gekozen{' '}
              {selectedModuleIds.length === 1 ? 'module' : 'modules'} — dus ook
              onderdelen die je misschien niet bedoelde.
            </div>
          </div>}

        <div className="flex items-center justify-between mb-2" data-id="element-9271" data-mp="33epy772kx5mq">
          <div className="flex items-center gap-2" data-id="element-9272" data-mp="37f0ebck35cfb">
            <span className="text-sm font-bold text-indigo-600" data-id="element-9273" data-mp="37eqnjmr7v18k">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-9274" data-mp="2ot2axd12xkzn">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-9275" data-mp="2xre7rwsjhf0j">
            <CheckSquare className="w-3.5 h-3.5" data-id="element-9276" data-mp="37oiy88tf3wn5" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        <div className="relative mb-3" data-id="element-9277" data-mp="32owkzzdlagec">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9278" data-mp="3gidv6tkdygrp" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-9279" data-mp="3cgaf1rmruo03" />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-9280" data-mp="3lijo0d2e69bd">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-9281" data-mp="36s2csma2vyzp">
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-9282" data-mp="3fu36xaa0m4x5">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-9283" data-mp="3frpg5dmdbeve">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-9284" data-mp="1dw9jhn7p18xo" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-9285" data-mp="1962wtswqryi2" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-9286" data-mp="146q1ejrt1j3v">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-9287" data-mp="z9gcr6c8pbh3">
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-9288" data-mp="1ivw62n9ew1j5">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-indigo-500 border-indigo-500 text-white' : isPartiallySelected ? 'bg-indigo-100 border-indigo-400 text-indigo-500' : 'border-slate-300 bg-white'}`} data-id="element-9289" data-mp="bjdkoq8bs5ih">
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-9290" data-mp="1xlatto0bs7pz" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-9291" data-mp="22iu82nx0wkhx" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-9292" data-mp="2u9qhfejvk70g">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <div key={f.familyId} onClick={() => toggleFamily(f.familyId)} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-9293" data-mp="2lgx1rg0in5m2">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9294" data-mp="3jc92emk1ahnm">
                            {isSelected && <Check className="w-3 h-3" data-id="element-9295" data-mp="34eqd9j3g22fn" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-9296" data-mp="34gjlv3xmawqa">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-9297" data-mp="30eg828nh7g1u">
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-9298" data-mp="tl460rgd37ha">
                            {f.levelCode}
                          </span>
                        </div>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-9299" data-mp="1xts1wm4wtnz7">
              <div className="mb-3 flex justify-center" data-id="element-9300" data-mp="1tqvr3hlm2w5t">
                <DjayNarrator size="md" emotion="thinking" data-id="element-9301" data-mp="1tthz1icezdok" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  const renderStep3 = () => {
    return <div className="flex flex-col gap-4 pb-4" data-id="element-9302" data-mp="2hhhnucq60u0i">
        <div className="flex items-center gap-2.5" data-id="element-9303" data-mp="2hg6ii8h2ne7a">
          <p className="text-sm font-medium text-slate-600" data-id="element-9304" data-mp="2chd84ox303bc">
            Bijna klaar! Kies de leerlingen en voeg details toe.
          </p>
        </div>

        <div data-id="element-9305" data-mp="2qfl9ra52bssu">
          <label className="text-xs font-semibold text-slate-500 mb-1 block" data-id="element-9306" data-mp="2qfblbqzo1tp9">
            Titel (optioneel)
          </label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Bijv. Breuken oefenen" className="h-9 text-sm" data-id="element-9307" data-mp="353v472pjjqxm" />
        </div>

        <div className="grid grid-cols-2 gap-2" data-id="element-9308" data-mp="2w4galue2v08w">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9309" data-mp="2w5rf5wfckc12">
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9310" data-mp="2r6ol170wnq7v" />
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-9311" data-mp="2he0rcl5822dd">
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9312" data-mp="28elyxg5hvhq8">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-indigo-200 bg-white'}`} data-id="element-9313" data-mp="dynk8vekkg5b">
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-indigo-500' : 'text-slate-400'}`} data-id="element-9314" data-mp="82258wxwa8h" />
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-9315" data-mp="rxwtfb4cy9zs">
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9316" data-mp="ix8n72l7hdxg">Kies leerlingen</div>
          </div>
        </div>

        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-9317" data-mp="38nvgpi7pnbmw">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-9318" data-mp="33m62xut3sjv4">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[150px] overflow-y-auto divide-y divide-slate-100" data-id="element-9319" data-mp="1o51gsc8ax38r">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <div key={student.id} onClick={() => toggleStudent(student.id)} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/40' : 'hover:bg-slate-50'}`} data-id="element-9320" data-mp="1wyo6plptvca1">
                    <div className="flex items-center gap-2.5" data-id="element-9321" data-mp="2fv5yg34ofc6a">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-9322" data-mp="2fumnx7hcvq2a">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-9323" data-mp="3mccg68q2ixt2">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9324" data-mp="3id3n762ur3yk">
                      {isSelected && <Check className="w-3 h-3" data-id="element-9325" data-mp="33mwbrab9il7d" />}
                    </div>
                  </div>;
          })}
            </div>
          </div>}

        <div className="flex items-center gap-3" data-id="element-9326" data-mp="3dc7iwrel9fsm">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-9327" data-mp="3de0tuiw8im6f" />
          <div className="flex-1" data-id="element-9328" data-mp="38kfuoniwnm5n">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9329" data-mp="38jeds2f7k5g0">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-9330" data-mp="2fxbuqwyo5y0q" />
          </div>
        </div>

        <div className="flex items-start gap-3" data-id="element-9331" data-mp="me35mgj6dqdt">
          <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-2" data-id="element-9332" data-mp="mfe9egcmey4x" />
          <div className="flex-1" data-id="element-9333" data-mp="qggaz3u0fma2">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9334" data-mp="qdbylj6e5k8b">
              Bericht voor leerlingen (optioneel)
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Bijv. Succes met oefenen!" className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none h-20" data-id="element-9335" data-mp="1j4c45ykdew23" />
          </div>
        </div>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Huiswerk Opgeven" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-100 text-indigo-700" data-id="element-9337" data-mp="20cd1zpyqawxb">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-9336" data-mp="3utwkys1rsvta">
      <div className="flex flex-col h-full" data-id="element-9338" data-mp="1qmaaxvb63t1m">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-9339" data-mp="1mmjebayymip6">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-9340" data-mp="1hmn58xbeo12r">
              <DjayNarrator size="lg" emotion="proud" data-id="element-9341" data-mp="13pi5tg6760gh" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-9342" data-mp="321yyoow8ww3h">
              <CheckCircle2 className="w-8 h-8" data-id="element-9343" data-mp="tlbfk99uoohu" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-9344" data-mp="xkakhtvl0l9a">
              Huiswerk opgegeven! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-9345" data-mp="es7zy7ftkktr">
              Het huiswerk voor{' '}
              <span className="font-bold" data-id="element-9346" data-mp="wznq2ira8e0b">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet —{' '}
              <span className="font-bold" data-id="element-9347" data-mp="1kmvvr1smhp2v">
                {selectedFamilyIds.length > 0 ? `${selectedFamilyIds.length} onderdelen` : `alle onderdelen van ${selectedModuleIds.length === 1 ? 'het gekozen vak' : 'de gekozen vakken'}`}
              </span>
              .
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-9348" data-mp="3veoro0u5dbmw">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-9349" data-mp="3la8bt7go3uj3">
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-9350" data-mp="2xl5vc2xrs7q6">
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {renderStepIndicator()}
            <div className="flex-1 overflow-hidden relative" data-id="element-9351" data-mp="mv7cxx56tb07">
              <AnimatePresence mode="wait" custom={direction} data-id="element-9352" data-mp="rspakrdr83sw">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-9353" data-mp="rs7go1eke1o5">
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex-shrink-0 bg-white" data-id="element-9354" data-mp="2malvzp22z1j8">
              {renderSummaryStrip()}
              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-9355" data-mp="2r7ca46ubrn20">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-9356" data-mp="rvt0dq176ara">
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>
                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9357" data-mp="35cmdvtl8yiyp">
                    Volgende <ChevronRight className="w-4 h-4 ml-1" data-id="element-9358" data-mp="3t405fxbkqaim" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9359" data-mp="3p1n21fkauop0">
                    {isSubmitting ? 'Bezig...' : 'Opgeven'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}